import useToken from 'hooks/useToken';
import React, { useEffect, useRef, useState, useContext } from 'react';
import DatePicker from 'react-datepicker';
import { Card, Col, Row, Form, Spinner, InputGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'context/providers/ToastProvider';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import PagoRecibidoCard from './PagoRecibidoCard';
import { ContabilidadLayout } from 'akasia-components/Contabilidad/common/ContabilidadLayout';
import { ContabilidadContext } from 'akasia-components/Contabilidad/common/ContabilidadContext';

const ListaPagosRecibidosContent = () => {

    const { showErrorToast } = useToast();
    const { token, setToken } = useToken();
    const { selectedCompany, globalDateQuarter: dateQuarter, setGlobalDateQuarter: setDateQuarter } = useContext(ContabilidadContext);
    const [cargando, setCargando] = useState(true);
    const [data, setData] = useState([]);

    const [filtroEstadoComprobante, setFiltroEstadoComprobante] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const debounceTimer = useRef(null);

    // Estados para infinite scroll
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(20);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    // Estado para pagos seleccionados
    const [pagosSeleccionados, setPagosSeleccionados] = useState([]);

    /**
     * Función para resetear y obtener datos (por si se cambia filtro como RFC o Mes)
     */
    const resetAndFetch = () => {
        setPageNumber(1);
        setHasMoreData(true);
        setData([]);
        setPagosSeleccionados([]);
        obtenerPagosRecibidos(1, false);
    };

    /**
     *  Obtiene una vez los pagos recibidos cuando el RFC cambia
     */
    useEffect(() => {
        if (!selectedCompany?.vRFC) {
            setData([]);
            setTotalRecords(0);
            return;
        }

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        if (busqueda !== '') {
            debounceTimer.current = setTimeout(() => {
                resetAndFetch();
            }, 500);
        } else {
            resetAndFetch();
        }

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [selectedCompany?.vRFC, dateQuarter, filtroEstadoComprobante, busqueda]);

    /**
     * Función para manejar el cambio de fecha
     */
    const handleDateChange = (date) => {
        setDateQuarter(date);
    };

    // obtenerRfcReceptor ha sido eliminado a favor de estado global

    // Obtener pagos recibidos con filtrado por mes y año y paginación
    const obtenerPagosRecibidos = async (page = 1, append = false, explicitDate = null) => {
        if (!append) {
            setCargando(true);
        } else {
            setLoadingMore(true);
        }

        var date_ = explicitDate || dateQuarter;

        var month = new Date(date_).getMonth() + 1;
        var year = new Date(date_).getFullYear();

        // Asegura que el mes tenga siempre 2 dígitos (ej. 02, 03, ..., 12)
        var formattedMonth = month < 10 ? '0' + month : '' + month;

        const requestData = {
            year: '' + year,
            month: formattedMonth,
            rfc: selectedCompany?.vRFC || '',
            pageNumber: page,
            pageSize: pageSize,
            estadoComprobante: filtroEstadoComprobante,
            searchTerm: busqueda
        };

        const response = await fetch("/api/contabilidad/centro-xml/PagosRecibidos/obtenerPagosRecibidos", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestData)
        }).catch((error) => {
            showErrorToast('Hubo un problema con la petición Fetch:' + error);
        });

        if (response && response.ok) {
            const resData = await response.json();

            if (resData.iResponseType == 1) {
                var ObjDetalles = JSON.parse(resData.detalles);
                const nuevosPagos = ObjDetalles.table2 || [];

                if (append) {
                    setData(prev => [...prev, ...nuevosPagos]);
                } else {
                    setData(nuevosPagos);
                    setPageNumber(1);
                }

                // Asumiendo que el totalRecords viene en table3, de lo contrario usamos la longitud
                const totalRecordsFromResponse = ObjDetalles.table3?.[0]?.totalRecords || (append ? data.length + nuevosPagos.length : nuevosPagos.length);
                setTotalRecords(totalRecordsFromResponse);

                // Si la longitud de la página retornada es menor al tamaño de página, ya no hay más
                if (nuevosPagos.length < pageSize) {
                    setHasMoreData(false);
                } else {
                    setHasMoreData((page * pageSize) < totalRecordsFromResponse);
                }

            } else {
                showErrorToast(resData.vMessage);
                if (!append) {
                    setData([]);
                    setHasMoreData(false);
                }
            }
        }
        else if (response) {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
            if (!append) {
                setData([]);
                setHasMoreData(false);
            }
        }

        if (!append) {
            setCargando(false);
        } else {
            setLoadingMore(false);
        }
    };

    /**
     * Función para cargar más datos (infinite scroll)
     */
    const cargarMasDatos = () => {
        if (hasMoreData && !loadingMore && !cargando) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            obtenerPagosRecibidos(nextPage, true);
        }
    };

    /**
     * Función para manejar la selección/deselección
     */
    const toggleSeleccionPago = (idFacturaPago) => {
        setPagosSeleccionados(prev => {
            if (prev.includes(idFacturaPago)) {
                return prev.filter(id => id !== idFacturaPago);
            } else {
                return [...prev, idFacturaPago];
            }
        });
    };

    /**
     * Componente para el trigger de infinite scroll
     */
    const InfiniteScrollTrigger = () => {
        const triggerRef = useRef();

        useEffect(() => {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMoreData && !loadingMore && !cargando) {
                        cargarMasDatos();
                    }
                },
                { threshold: 0.1 }
            );

            if (triggerRef.current) {
                observer.observe(triggerRef.current);
            }

            return () => {
                if (triggerRef.current) {
                    observer.unobserve(triggerRef.current);
                }
            };
        }, [hasMoreData, loadingMore, cargando]);

        if (!hasMoreData) return null;

        return (
            <div ref={triggerRef} className="text-center py-4">
                {loadingMore ? (
                    <div>
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <div className="text-muted small">Cargando más pagos...</div>
                    </div>
                ) : (
                    <div className="text-muted small">Desplázate para cargar más</div>
                )}
            </div>
        );
    };

    return (
        <Row>
            <Col>
                <Card className="mb-3">
                    <Card.Header className="pb-0 bg-light">
                        <Row className="align-items-end g-2 mb-3">
                                {/* Grupo DatePicker */}
                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group>
                                        <Form.Label className="text-700 mb-1">Mes</Form.Label>
                                        <DatePicker
                                            selected={dateQuarter}
                                            onChange={handleDateChange}
                                            dateFormat="MMMM yyyy - QQQ"
                                            className="form-control w-100"
                                            showMonthYearPicker
                                        />
                                    </Form.Group>
                                </Col>

                                {/* Búsqueda */}
                                <Col xs={12} md={4}>
                                    <Form.Group>
                                        <Form.Label className="text-700 mb-1">Buscar pagos</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FontAwesomeIcon icon={faSearch} />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="RFC, Razón social, Folio..."
                                                value={busqueda}
                                                onChange={(e) => setBusqueda(e.target.value)}
                                            />
                                            {busqueda && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setBusqueda('')}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                
                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group>
                                        <Form.Label className="text-700 mb-1">Estado</Form.Label>
                                        <Form.Select
                                            value={filtroEstadoComprobante}
                                            onChange={(e) => setFiltroEstadoComprobante(e.target.value)}
                                        >
                                            <option value="">Todos</option>
                                            <option value="1">Activos</option>
                                            <option value="0">Cancelados</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body className="p-0">
                        {cargando ? (
                            <div className="p-4">
                                {[...Array(5)].map((_, index) => (
                                    <Card key={index} className="mb-3">
                                        <Card.Body>
                                            <Row>
                                                <Col xs={12} md={2}>
                                                    <Skeleton height={30} width={80} className="mb-2" />
                                                    <Skeleton height={15} width={60} />
                                                </Col>
                                                <Col xs={12} md={6}>
                                                    <Skeleton height={20} className="mb-2" />
                                                    <Skeleton height={15} width={200} />
                                                    <Skeleton height={15} width={150} />
                                                </Col>
                                                <Col xs={12} md={4} className="text-end">
                                                    <Skeleton height={25} width={100} className="mb-2" />
                                                    <Skeleton height={20} width={60} />
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </div>
                        ) : data.length > 0 ? (
                            <div className="p-3">
                                <div className="mb-3 d-flex justify-content-between align-items-center">
                                    <small className="text-muted">
                                        Mostrando {data.length} de {totalRecords} pagos recibidos
                                    </small>
                                    {pagosSeleccionados.length > 0 && (
                                        <small className="text-primary fw-bold">
                                            {pagosSeleccionados.length} pago{pagosSeleccionados.length !== 1 ? 's' : ''} seleccionado{pagosSeleccionados.length !== 1 ? 's' : ''}
                                        </small>
                                    )}
                                </div>
                                {data.map((pago) => (
                                    <PagoRecibidoCard
                                        key={pago.idFacturaPagoRecibido}
                                        pago={pago}
                                        isSelected={pagosSeleccionados.includes(pago.idFacturaPagoRecibido)}
                                        onToggleSelection={toggleSeleccionPago}
                                    />
                                ))}
                                <InfiniteScrollTrigger />
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <div className="mb-3">
                                    <FontAwesomeIcon
                                        icon={faFileInvoiceDollar}
                                        size="3x"
                                        className="text-muted"
                                    />
                                </div>
                                <h5 className="text-muted">No se encontraron pagos recibidos</h5>
                                <p className="text-muted">
                                    No hay pagos registrados para el periodo y RFC seleccionados
                                </p>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

const ListaPagosRecibidos = () => {
    return (
        <ContabilidadLayout
            title={
                <><FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />Pagos Recibidos</>
            }
        >
            <ListaPagosRecibidosContent />
        </ContabilidadLayout>
    );
};

export default ListaPagosRecibidos;
